/** IoT设备与报警相关类型 */

/** IoT设备类型 */
export enum IoTDeviceType {
  TEMP_HUMIDITY = 'TEMP_HUMIDITY',
  GPS_TRACKER = 'GPS_TRACKER',
  SHOCK_SENSOR = 'SHOCK_SENSOR',
  MULTI_SENSOR = 'MULTI_SENSOR',
  REEFER_CONTAINER = 'REEFER_CONTAINER',
  REEFER_TRUCK = 'REEFER_TRUCK',
}

/** IoT设备状态 */
export enum IoTDeviceStatus {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  LOW_BATTERY = 'LOW_BATTERY',
  MALFUNCTION = 'MALFUNCTION',
}

/** 报警类型 */
export enum AlertType {
  TEMP_OUT_OF_RANGE = 'TEMP_OUT_OF_RANGE',
  HUMIDITY_OUT_OF_RANGE = 'HUMIDITY_OUT_OF_RANGE',
  SHOCK_DETECTED = 'SHOCK_DETECTED',
  GEO_FENCE_BREACH = 'GEO_FENCE_BREACH',
  DEVICE_OFFLINE = 'DEVICE_OFFLINE',
  LOW_BATTERY = 'LOW_BATTERY',
}

/** 报警严重程度 */
export enum AlertSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  CRITICAL = 'CRITICAL',
}

/** 报警处理状态 */
export enum AlertHandleStatus {
  PENDING = 'PENDING',
  ACKNOWLEDGED = 'ACKNOWLEDGED',
  RESOLVED = 'RESOLVED',
}

/** 通知渠道路由 */
export enum NotifyChannelType {
  EMAIL = 'EMAIL',
  DINGTALK = 'DINGTALK',
  WEBHOOK = 'WEBHOOK',
}

/** 传感器读数 */
export interface SensorReading {
  timestamp: string;
  temperature: number;
  humidity: number;
  co2: number;
  shock: number;
  lightExposure: number;
}

// ===== 真实后端 DTO（对齐 blu-iot / blu-data / blu-ai 实体） =====

/** 设备 DTO（对齐 DevDevice 表） */
export interface DeviceDto {
  id: string;
  deviceNo: string;
  deviceName: string;
  typeCode: string;
  typeName: string;
  containerId: string;
  containerNo: string;
  status: string;
  batteryLevel: number;
  signalStrength: number;
  orgId: string;
  installedAt: string;
  lastReadingAt: string;
  /** 最新传感器读数 */
  lastReading?: IotReadingDto;
}

/** 设备影子 DTO */
export interface DeviceShadowDto {
  deviceId: string;
  deviceNo: string;
  /** 期望状态 JSON */
  desired: Record<string, unknown>;
  /** 上报状态 JSON */
  reported: Record<string, unknown>;
  /** 差异 JSON */
  delta: Record<string, unknown>;
  version: number;
  updatedAt?: string;
}

/** 传感器读数 DTO（对齐 iot_readings 表） */
export interface IotReadingDto {
  id: string;
  deviceId: string;
  deviceNo: string;
  /** 温度（℃）— CT7001 等真实设备必填；模拟设备必填 */
  temperature: number;
  /** 湿度（%RH）— 真实 IoT 设备可能不采该指标，为 null */
  humidity: number | null;
  /** CO2（ppm）— 真实 IoT 设备可能不采该指标，为 null */
  co2: number | null;
  /** 震动（g）— 真实 IoT 设备可能不采该指标，为 null */
  shock: number | null;
  /** 光照（lux）— 真实 IoT 设备可能不采该指标，为 null */
  lightExposure: number | null;
  /** GPS 纬度（GCJ02）— 室内/无定位设备为 null */
  gpsLatitude: number | null;
  /** GPS 经度（GCJ02）— 室内/无定位设备为 null */
  gpsLongitude: number | null;
  /** GPS 速度（km/h）— 无定位设备为 null */
  gpsSpeed: number | null;
  /** GPS 航向（度）— 无定位设备为 null */
  gpsHeading: number | null;
  /** 中文地址（设备上报，如 "山东省青岛市黄岛区汉江路靠近泛亚场地"；无/无效为 null） */
  address: string | null;
  /** 电量（%）— 真实 IoT 设备可能未上送该指标，为 null */
  batteryLevel: number | null;
  /** 信号强度（%）— 真实 IoT 设备可能未上送该指标，为 null */
  signalStrength: number | null;
  readingAt: string;
}

/** 报警 DTO（对齐 dev_alert 表） */
export interface IotAlertDto {
  id: string;
  deviceId: string;
  deviceNo: string;
  containerId: string;
  containerNo: string;
  type: string;
  severity: string;
  message: string;
  reading: IotReadingDto | null;
  handleStatus: AlertHandleStatus;
  acknowledgedBy: string;
  createdAt: string;
  acknowledgedAt: string;
  resolvedAt: string;
}

/** 报警规则 DTO（对齐 dev_alert_rule 表） */
export interface AlertRuleDto {
  id: string;
  ruleName: string;
  ruleCode: string;
  alertType: string;
  severity: string;
  /** 阈值条件 JSONB */
  conditions: string;
  /** 是否启用 */
  enabled: number;
  orgId: string;
  createdAt: string;
  updatedAt: string;
}

/** 通知渠道配置 DTO */
export interface NotifyConfigDto {
  id: string;
  channelType: NotifyChannelType;
  channelName: string;
  enabled: number;
  /** 渠道配置 JSONB */
  configJson: string;
  orgId: string;
  createdAt: string;
  updatedAt: string;
}

/** 设备分组 DTO（对齐 dev_device_group 表） */
export interface DeviceGroupDto {
  id: string;
  groupName: string;
  groupCode: string;
  description: string;
  deviceCount: number;
  orgId: string;
  createdAt: string;
  updatedAt: string;
}

/** 设备类型 DTO（对齐 dev_device_type 表） */
export interface DeviceTypeDto {
  id: string;
  typeCode: string;
  typeName: string;
  /** 图标标识 */
  icon?: string;
  /** 物模型 JSONB */
  modelJson: string;
  /** 所属组织：ORG_PLATFORM = 平台预置（租户只读），其余为租户自建 */
  orgId: string;
  createdAt: string;
  updatedAt: string;
}

/** 设备类型新增/编辑请求（id 为空=新增，非空=编辑；编辑时 typeCode 不可改） */
export interface DeviceTypeSaveRequest {
  id?: string;
  typeCode: string;
  typeName: string;
  icon?: string;
  /** 物模型 JSON 字符串（须为 JSON 对象） */
  modelJson?: string;
}

/** AI 对话 DTO */
export interface AiConversationDto {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  /** 工具调用 JSONB */
  toolCalls: string;
  /** 创建时间 ISO 串（服务端为带 Z 的绝对时刻，前端渲染按浏览器本地时区） */
  createdAt: string;
  /**
   * 结构化附加信息（仅实体直查响应携带，如按设备编号查实时温度；RAG/普通对话为 null）。
   * readingAt 为 ISO-8601 带 Z 的绝对时刻串，与 content 正文文本保持一致（历史回显一致）。
   */
  meta?: {
    deviceNo?: string;
    deviceName?: string;
    temperature?: number | string | null;
    humidity?: number | string | null;
    readingAt?: string;
  } | null;
}

/** AI 知识库文档 DTO */
export interface KbDocumentDto {
  id: string;
  kbId: string;
  fileName: string;
  fileSize: number;
  status: string;
  chunkCount: number;
  orgId: string;
  createdAt: string;
  updatedAt: string;
}

/** AI 知识库 DTO */
export interface KnowledgeBaseDto {
  id: string;
  kbName: string;
  kbCode: string;
  description: string;
  documentCount: number;
  orgId: string;
  createdAt: string;
  updatedAt: string;
}

/** 异常检测规则 DTO */
export interface AnomalyDetectionRuleDto {
  id: string;
  ruleName: string;
  ruleCode: string;
  metricType: string;
  algorithm: string;
  /** 参数 JSONB */
  params: string;
  enabled: number;
  orgId: string;
  createdAt: string;
  updatedAt: string;
}

// ===== 查询参数 DTO =====

/** 设备列表查询参数 */
export interface DeviceListQuery {
  page?: number;
  pageSize?: number;
  deviceNo?: string;
  containerId?: string;
  containerNo?: string;
  status?: string;
  typeCode?: string;
}

/** 报警列表查询参数 */
export interface AlertListQuery {
  page?: number;
  pageSize?: number;
  type?: string;
  severity?: string;
  handleStatus?: string;
  deviceNo?: string;
  startTime?: string;
  endTime?: string;
}

/** 数据读数查询参数 */
export interface ReadingQuery {
  deviceId: string;
  from?: string;
  to?: string;
  /** true 时仅返回有有效 GPS 坐标的读数（地图轨迹用）；温度曲线等传感器用途不传/传 false 取全量 */
  gpsOnly?: boolean;
  metric?: string;
  page?: number;
  pageSize?: number;
}

/** 知识库查询参数 */
export interface KbListQuery {
  page?: number;
  pageSize?: number;
  keyword?: string;
}

/** AI 对话请求体 */
export interface AiChatRequest {
  sessionId: string;
  message: string;
}

/** AI 会话摘要 */
export interface ChatSessionDto {
  sessionId: string;
  title: string;
  messageCount: number;
  lastMessageAt: string;
}

/** 设备绑定请求体 */
export interface DeviceBindRequest {
  deviceNo: string;
  containerNo: string;
}

// ===== 柜↔设备绑定（对接 blu-trade 代理 /container/{id}/iot/* → blu-iot） =====

/** 柜↔设备绑定请求体（前端 → blu-trade 代理） */
export interface ContainerBindRequest {
  /** 柜档案 ID（容器 UUID） */
  containerId: string;
  /** 设备编号（如 REEFER-001），由 blu-iot 反查 devices.id */
  deviceNo: string;
  /** 绑定类型：REEFER_MAIN / TEMP_SENSOR / GPS_TRACKER */
  boundType: string;
  /** 备注（可选） */
  remark?: string;
  /** 操作人（当前登录用户） */
  operator?: string;
}

/** 绑定记录响应 */
export interface ContainerBindResp {
  id: string;
  containerId: string;
  deviceId: string;
  boundType: string;
  status: string;
  /** ISO-8601 绑定时间 */
  boundAt?: string;
}

/** ACTIVE 绑定清单项 */
export interface ActiveBindVo {
  bindId: string;
  containerId: string;
  deviceId: string;
  deviceNo: string;
  deviceType: string;
  boundType: string;
  boundAt?: string;
}

/** 实时聚合项（含温湿度与 GPS） */
export interface RealtimeItem {
  boundType: string;
  deviceNo: string;
  deviceType: string;
  deviceId: string;
  temperature?: number | null;
  humidity?: number | null;
  gpsLatitude?: number | null;
  gpsLongitude?: number | null;
  gpsSpeed?: number | null;
  gpsHeading?: number | null;
  /** 中文地址（设备上报） */
  address?: string | null;
  /** ISO-8601 最近读数时间 */
  lastReadingAt?: string;
}

/** 实时聚合视图 */
export interface ContainerRealtimeVo {
  containerId: string;
  items: RealtimeItem[];
}

/** 温度报告项 */
export interface TempReportItem {
  deviceNo: string;
  deviceType: string;
  boundType: string;
  tempTarget?: number | null;
  upperBound?: number | null;
  lowerBound?: number | null;
  minTemp?: number | null;
  maxTemp?: number | null;
  avgTemp?: number | null;
  sampleCount?: number | null;
  exceedUpperSeconds?: number | null;
  belowLowerSeconds?: number | null;
  violationPeriods?: number | null;
}

/** 温度报告结果 */
export interface TempReportResult {
  containerId: string;
  arriveAt?: string;
  items: TempReportItem[];
}

/** 设备新增/编辑请求体（新增/编辑合一：id 为空新增，非空编辑） */
export interface DeviceSaveRequest {
  /** 设备 ID（编辑时必填，新增时留空） */
  id?: string;
  /** 设备编号（唯一） */
  deviceNo: string;
  /** 设备名称 */
  name: string;
  /** 设备类型编码（对应 iot_device_type 枚举值） */
  type: string;
  /** 状态（默认 ONLINE） */
  status?: string;
  /** 关联柜 ID */
  containerId?: string;
  /** 关联柜号 */
  containerNo?: string;
  /** 电量 */
  batteryLevel?: number;
  /** 信号强度 */
  signalStrength?: number;
  /** 协议类型（TCP/MQTT...） */
  protocolType?: string;
  /** 安装时间（ISO-8601） */
  installedAt?: string;
  /** 连接信息（jsonb） */
  connectionInfo?: Record<string, unknown>;
  /** 扩展属性（jsonb） */
  properties?: Record<string, unknown>;
}

// ===== D05 IoT设备与报警中心专用类型（保留向后兼容） =====

/** 时间范围（装柜时间锚定）：24h/3d/7d 为「装柜后 N」档，full 为「装柜 → 交付(或现在)」全窗口 */
export type TimeRange = '24h' | '3d' | '7d' | 'full';

/** IoT设备（兼容旧骨架，逐步迁移到 DeviceDto） */
export interface IoTDevice {
  id: string;
  deviceNo: string;
  type: IoTDeviceType;
  containerId: string;
  /** 绑定柜号（展示用，实际 ISO 柜号，如 "MSCU-8123456"） */
  containerNo: string;
  lastReading: SensorReading;
  readingHistory: SensorReading[];
  batteryLevel: number;
  signalStrength: number;
  status: IoTDeviceStatus;
  installedAt: string;
}

/** IoT报警（兼容旧骨架，逐步迁移到 IotAlertDto） */
export interface IoTAlert {
  id: string;
  deviceId: string;
  containerId: string;
  type: AlertType;
  severity: AlertSeverity;
  message: string;
  reading: SensorReading;
  acknowledged: boolean;
  acknowledgedBy: string;
  createdAt: string;
  resolvedAt: string;
}

/** 设备统计卡片数据 */
export interface IoTDeviceStats {
  /** 在用设备总数 */
  totalDevices: number;
  /** 在线数 */
  onlineDevices: number;
  /** 离线数 */
  offlineDevices: number;
  /** 在线率 0-100 */
  onlineRate: number;
  /** 温度报警数 */
  tempAlertCount: number;
  /** 电量预警数（<20%） */
  batteryWarningCount: number;
}

/** 温度曲线指标 */
export interface TempIndicator {
  /** 当前温度 */
  currentTemp: number;
  /** 目标温度 */
  targetTemp: number;
  /** 超限分钟数 */
  exceedMinutes: number;
  /** 当前湿度 */
  humidity: number;
  /** 温度单位 */
  tempUnit: string;
}

/** GIS 航线锚点 */
export interface GISRoutePoint {
  /** 锚点名称：青岛 / 当前位置 / 迪拜 */
  name: string;
  lat: number;
  lng: number;
  /** 缩写标签 "QD" / "POS" / "DXB" */
  label: string;
  /** 是否为当前位置 */
  isCurrent: boolean;
}

/** GIS 航线数据 */
export interface GISRouteData {
  routeId: string;
  /** 航线名称 "青岛 → 迪拜" */
  routeName: string;
  /** 3 个锚点：[青岛, 当前位置, 迪拜] */
  points: GISRoutePoint[];
  /** 已航行海里数 */
  sailedNauticalMiles: number;
  /** 剩余海里数 */
  remainingNauticalMiles: number;
  /** 预计到达 ISO 8601 */
  estimatedArrival: string;
  /** 进度 0-100 */
  progressPercent: number;
}

/** 温度图表数据点 */
export interface TempChartDataPoint {
  /** 时间标签 HH:mm 或 MM/DD HH:mm */
  time: string;
  temperature: number;
  /** 上限参考线值 */
  upperThreshold: number;
  /** 下限参考线值 */
  lowerThreshold: number;
}


/** AI LLM 供应商（C4 动态模型热切换，ai_llm_provider 表） */
export interface AiLlmProviderDto {
  id: string;
  /** 供应商编码：ollama / mimo / deepseek / custom-xxx */
  providerCode: string;
  providerName: string;
  /** OLLAMA=本地 Ollama；OPENAI=OpenAI 兼容（MiMo/DeepSeek/自定义） */
  providerType: 'OLLAMA' | 'OPENAI' | string;
  /** API 根地址（OPENAI 兼容填服务商根，不带 /v1） */
  baseUrl: string;
  modelName: string;
  temperature: number;
  maxTokens: number;
  isActive: boolean;
  status: number;
  sortOrder: number;
  /** NONE=未探测 / OK=最近成功 / ERROR=最近失败 */
  lastStatus: 'NONE' | 'OK' | 'ERROR' | string;
  lastError?: string;
  lastLatencyMs?: number;
  lastCheckedAt?: string;
  remark?: string;
  createdAt: string;
  updatedAt: string;
  /** 打码密钥（如 ****abcd），仅用于展示「已配置」 */
  apiKeyMasked?: string;
  /** 是否已配置 api-key */
  apiKeySet?: boolean;
}

/** AI LLM 供应商保存请求体（apiKey 回传留空 = 不修改） */
export interface AiLlmProviderSaveBody {
  providerName: string;
  providerType: 'OLLAMA' | 'OPENAI';
  baseUrl?: string;
  apiKey?: string;
  modelName: string;
  temperature?: number;
  maxTokens?: number;
  remark?: string;
}

/** 当前生效模型状态（GET /ai/llm/status） */
export interface AiLlmStatusDto {
  active: {
    providerCode: string;
    providerName: string;
    providerType: string;
    modelName: string;
    baseUrl: string;
    apiKeySet: boolean;
    lastStatus: string;
  } | null;
  enabledCount: number;
  builtinFallbackOllama: boolean;
}

/** LLM 连接测试结果（POST /ai/llm/{id}/test） */
export interface AiLlmTestResult {
  success: boolean;
  message: string;
  latencyMs: number;
}
